import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, CalendarClock, Link, Loader2, Mail, Plus, Search, ShieldCheck, ThumbsDown, UserRound, X,
} from 'lucide-react';
import { apiFetch } from '../api';
import EmailCenterView from './EmailCenterView';
import ScheduledReportsView from './ScheduledReportsView';
import { setCustomSourceFragments } from '../constants/trustedSources';

function uniqueUrls(values) {
  const seen = new Set();
  return (values || [])
    .map(v => String(v || '').trim())
    .filter(Boolean)
    .filter(url => {
      const key = url.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function normalizeSource(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    return parsed.hostname.replace(/^www\./i, '').toLowerCase();
  } catch {
    return raw.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0].toLowerCase();
  }
}

function formatSourceName(source) {
  const normalized = normalizeSource(source);
  if (!normalized) return 'Source';
  const names = {
    'economictimes.indiatimes.com': 'Economic Times',
    'moneycontrol.com': 'Moneycontrol',
    'livemint.com': 'Livemint',
    'ndtv.com': 'NDTV',
    'reuters.com': 'Reuters',
    'bloomberg.com': 'Bloomberg',
  };
  if (names[normalized]) return names[normalized];
  if (normalized.includes('economictimes')) return 'Economic Times';
  return normalized
    .split('.')[0]
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}


function SourceChannelsSection({ sources, customSources, onChange }) {
  const [sourceInput, setSourceInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const customSet = useMemo(() => new Set((customSources || []).map(normalizeSource)), [customSources]);
  const visibleSources = useMemo(
    () => uniqueUrls((sources || []).map(normalizeSource)),
    [sources],
  );

  const updateSources = async (nextCustomSources) => {
    setSaving(true);
    setError('');
    try {
      await onChange(nextCustomSources);
    } catch (err) {
      setError(err.message || 'Could not save source');
    } finally {
      setSaving(false);
    }
  };

  const addSource = () => {
    const normalized = normalizeSource(sourceInput);
    if (!normalized) return;
    updateSources(uniqueUrls([...(customSources || []), normalized]));
    setSourceInput('');
  };

  const removeCustomSource = (source) => {
    updateSources((customSources || []).filter(item => normalizeSource(item) !== normalizeSource(source)));
  };

  return (
    <div className="rounded-md border border-[#e1e5ec] bg-white overflow-hidden">
      <div className="px-5 py-4 border-b border-[#e1e5ec]">
        <h3 className="text-sm font-semibold text-[#0d1524]">News source domains</h3>
        <p className="text-xs text-[#5b6780] mt-1">These are the sites used for collection. Add another news domain to include it in future searches.</p>
      </div>

      <div className="p-5">
        <div className="flex gap-2 mb-4">
          <input
            value={sourceInput}
            onChange={e => setSourceInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addSource()}
            placeholder="ndtv.com"
            className="flex-1 text-[13px] border border-[#e1e5ec] rounded-md px-3 focus:outline-none focus:border-[#1c4ed8] h-8"
          />
          <button
            type="button"
            onClick={addSource}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 text-[13px] font-medium text-white bg-[#1c4ed8] border border-transparent rounded-md hover:brightness-110 disabled:opacity-50 h-8"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add
          </button>
        </div>
        {error && <p className="text-xs text-[#bd2f2b] mb-3">{error}</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[360px] overflow-y-auto pr-1">
          {visibleSources.map(source => {
            const custom = customSet.has(normalizeSource(source));
            return (
              <div key={source} className="flex items-center gap-3 rounded-lg border border-[#e1e5ec] bg-[#f6f7fa] px-3 py-2.5">
                <Link size={14} className="text-[#76829a] flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#0d1524] truncate">{formatSourceName(source)}</p>
                  <p className="text-xs text-[#76829a] truncate">{source}</p>
                </div>
                {custom ? (
                  <button
                    type="button"
                    onClick={() => removeCustomSource(source)}
                    className="p-1.5 rounded-lg text-[#76829a] hover:text-[#bd2f2b] hover:bg-[#fae8e7] transition"
                  >
                    <X size={14} />
                  </button>
                ) : (
                  <span className="text-[11px] font-medium text-[#45516a] bg-white border border-[#e1e5ec] rounded px-1.5 py-0.5">
                    Default
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── Section: Keywords ─── */

function PeopleSettings({ onRefreshRecords }) {
  const [people, setPeople] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState('');
  const [searchingId, setSearchingId] = useState(null);
  const [searchStatus, setSearchStatus] = useState('');
  const [filterInput, setFilterInput] = useState('');
  const [verifyingId, setVerifyingId] = useState(null);
  const [profileDraft, setProfileDraft] = useState(null);
  const [personRecords, setPersonRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const saveTimeoutRef = useRef(null);
  const saveRequestIdRef = useRef(0);

  useEffect(() => () => clearTimeout(saveTimeoutRef.current), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch('/settings/people');
        if (!cancelled) {
          setPeople(data || []);
          setSelectedId(data?.[0]?.id || null);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load people');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selected = people.find(person => person.id === selectedId) || null;

  useEffect(() => {
    setProfileDraft(null);
    setPersonRecords([]);
    if (!selected?.name || selected.name === 'New person') return;
    let cancelled = false;
    (async () => {
      setRecordsLoading(true);
      try {
        const data = await apiFetch(`/intelligence?company=${encodeURIComponent(selected.name)}&limit=10`);
        if (!cancelled) setPersonRecords(data.results || []);
      } catch {
        // Non-critical: recent-results preview just stays empty.
      } finally {
        if (!cancelled) setRecordsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId]);

  const persist = async (next) => {
    setError('');
    // Snapshot the version *before* touching state - updateSelected bumps
    // this on every keystroke, including ones that happen while this
    // request is in flight, so we can tell afterwards whether the user
    // has kept typing since this particular save was kicked off.
    const expectedVersion = saveRequestIdRef.current;
    setPeople(next);
    try {
      const saved = await apiFetch('/settings/people', {
        method: 'PUT',
        body: JSON.stringify({ people: next }),
      });
      // Only apply the server response if the local state hasn't been
      // edited again since this save was kicked off - otherwise a slow
      // response for an old keystroke can land after a newer one and
      // stomp text the user already typed (e.g. mid-typing reverts).
      if (expectedVersion === saveRequestIdRef.current) setPeople(saved || []);
      return saved || [];
    } catch (err) {
      setError(err.message || 'Could not save people');
      return next;
    }
  };

  const addPerson = async () => {
    const person = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: 'New person',
      company: '',
      search_filters: [],
      updatedAt: new Date().toISOString(),
    };
    saveRequestIdRef.current += 1;
    await persist([person, ...people]);
    setSelectedId(person.id);
  };

  const updateSelected = (patch, { debounce = false } = {}) => {
    if (!selected) return;
    const next = people.map(person => (
      person.id === selected.id
        ? { ...person, ...patch, updatedAt: new Date().toISOString() }
        : person
    ));
    // Always reflect the keystroke locally right away so the input never
    // stalls or reverts while a save is in flight. Bumping the version here
    // (not just inside persist) is what lets an in-flight save recognize
    // that the user kept typing after it was sent.
    saveRequestIdRef.current += 1;
    setPeople(next);

    clearTimeout(saveTimeoutRef.current);
    if (debounce) {
      saveTimeoutRef.current = setTimeout(() => persist(next), 500);
    } else {
      persist(next);
    }
  };

  const removeSelected = async () => {
    if (!selected) return;
    const next = people.filter(person => person.id !== selected.id);
    saveRequestIdRef.current += 1;
    await persist(next);
    setSelectedId(next[0]?.id || null);
  };

  const addSearchFilter = (value) => {
    const filter = String(value || '').trim();
    if (!filter || !selected) return;
    const next = [...new Set([...(selected.search_filters || []), filter])];
    updateSelected({ search_filters: next });
    setFilterInput('');
  };

  const searchSelectedPerson = async () => {
    if (!selected) return;
    clearTimeout(saveTimeoutRef.current);
    setSearchingId(selected.id);
    setError('');
    setSearchStatus('');
    try {
      await persist(people);
      await apiFetch(`/settings/people/${encodeURIComponent(selected.id)}/search`, {
        method: 'POST',
      });
      setSearchStatus('Search successful — matching updates will keep showing up in Alerts and Overview.');
      setTimeout(() => setSearchStatus(''), 5000);
      onRefreshRecords?.();
      setTimeout(() => onRefreshRecords?.(), 8000);
    } catch (err) {
      setError(err.message || 'Could not start person search');
    } finally {
      setSearchingId(null);
    }
  };

  const verifySelectedPerson = async () => {
    if (!selected) return;
    clearTimeout(saveTimeoutRef.current);
    setVerifyingId(selected.id);
    setError('');
    setProfileDraft(null);
    try {
      await persist(people);
      const identity = await apiFetch(`/settings/people/${encodeURIComponent(selected.id)}/verify`, {
        method: 'POST',
      });
      setProfileDraft(identity);
    } catch (err) {
      setError(err.message || 'Could not verify this person');
    } finally {
      setVerifyingId(null);
    }
  };

  const confirmProfile = () => {
    if (!selected || !profileDraft) return;
    updateSelected({ identity_profile: profileDraft });
    setProfileDraft(null);
  };

  const rejectRecord = async (recordId) => {
    if (!selected) return;
    setRejectingId(recordId);
    setError('');
    try {
      await apiFetch(`/settings/people/${encodeURIComponent(selected.id)}/reject-record`, {
        method: 'POST',
        body: JSON.stringify({ record_id: recordId }),
      });
      setPersonRecords(prev => prev.filter(record => record.id !== recordId));
      const refreshed = await apiFetch('/settings/people');
      setPeople(refreshed || []);
      onRefreshRecords?.();
    } catch (err) {
      setError(err.message || 'Could not mark this as the wrong person');
    } finally {
      setRejectingId(null);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-5">
      <aside className="rounded-md border border-[#e1e5ec] bg-white overflow-hidden">
        <div className="p-3 border-b border-[#e1e5ec]">
          <button
            type="button"
            onClick={addPerson}
            className="w-full flex items-center justify-center gap-2 px-3 text-[13px] font-medium text-[#1c4ed8] bg-[#e7edfc] border border-[#c5d3f6] rounded-md hover:bg-[#e7edfc] transition h-8"
          >
            <Plus size={14} /> Add person
          </button>
        </div>
        <div className="max-h-[520px] overflow-y-auto p-2 space-y-1">
          {people.length === 0 ? (
            <p className="text-sm text-[#76829a] text-center py-10">No people added yet</p>
          ) : people.map(person => (
            <button
              key={person.id}
              type="button"
              onClick={() => setSelectedId(person.id)}
              className={`w-full text-left rounded-lg px-3 py-2.5 transition ${
 selectedId === person.id ? 'bg-[#e7edfc] text-[#1c4ed8]' : 'hover:bg-[#f6f7fa] text-[#45516a]'
 }`}
            >
              <p className="text-sm font-semibold truncate">{person.name || 'Unnamed person'}</p>
              <p className="text-xs text-[#76829a] truncate">{person.company || 'Person search'}</p>
            </button>
          ))}
        </div>
      </aside>

      <div className="space-y-5">
      {error && <p className="text-xs text-[#bd2f2b]">{error}</p>}
      <section className="rounded-md border border-[#e1e5ec] bg-white p-5">
        {!selected ? (
          <div className="text-center py-20 text-[#76829a]">
            <UserRound size={34} className="mx-auto mb-3 text-[#76829a]" />
            <p className="text-sm">Add a person to start deep search monitoring.</p>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-[#0d1524]">Individual person settings</h3>
                <p className="text-xs text-[#5b6780] mt-1">Name, company, and optional search filters</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={verifySelectedPerson}
                  disabled={verifyingId === selected.id}
                  className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#1c4ed8] bg-[#e7edfc] hover:bg-[#e7edfc] disabled:opacity-60 rounded-md px-3 h-8"
                >
                  {verifyingId === selected.id ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
                  {selected.identity_profile ? 'Re-verify identity' : 'Verify identity'}
                </button>
                <button
                  type="button"
                  onClick={searchSelectedPerson}
                  disabled={searchingId === selected.id}
                  title={selected.identity_profile ? '' : 'Verify identity first for pinpoint accuracy'}
                  className="inline-flex items-center gap-1.5 text-[13px] font-medium text-white bg-[#1c4ed8] border border-transparent hover:brightness-110 disabled:opacity-60 rounded-md px-3 h-8"
                >
                  {searchingId === selected.id ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
                  Search person
                </button>
                <button
                  type="button"
                  onClick={removeSelected}
                  className="text-[13px] font-medium text-[#bd2f2b] hover:bg-[#fae8e7] rounded-md px-3 h-8"
                >
                  Remove
                </button>
              </div>
            </div>
            {searchStatus && <p className="text-xs font-medium text-[#117049]">{searchStatus}</p>}

            {profileDraft && (
              <div className="rounded-md border border-[#c5d3f6] bg-[#e7edfc] p-4 space-y-2">
                <p className="text-xs font-semibold text-[#1c4ed8]">Is this the right person?</p>
                <p className="text-sm text-[#0d1524]">
                  {profileDraft.profile?.canonical_name || selected.name}
                  {profileDraft.profile?.current_role && profileDraft.profile.current_role !== 'unknown' && (
                    <> — {profileDraft.profile.current_role}</>
                  )}
                  {profileDraft.profile?.organizations?.length > 0 && (
                    <> at {profileDraft.profile.organizations.join(', ')}</>
                  )}
                </p>
                {profileDraft.profile?.location && profileDraft.profile.location !== 'unknown' && (
                  <p className="text-xs text-[#5b6780]">Location: {profileDraft.profile.location}</p>
                )}
                {profileDraft.evidence?.length > 0 && (
                  <ul className="text-xs text-[#5b6780] space-y-0.5">
                    {profileDraft.evidence.slice(0, 3).map(item => (
                      <li key={item.url} className="truncate">
                        <a href={item.url} target="_blank" rel="noreferrer" className="hover:text-[#1c4ed8] hover:underline">
                          {item.title || item.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={confirmProfile}
                    className="text-[13px] font-medium text-white bg-[#1c4ed8] border border-transparent hover:brightness-110 rounded-md px-3 h-8"
                  >
                    Yes, this is the person
                  </button>
                  <button
                    type="button"
                    onClick={() => setProfileDraft(null)}
                    className="text-[13px] font-medium text-[#5b6780] hover:bg-[#f6f7fa] rounded-md px-3 h-8"
                  >
                    Not them — let me edit
                  </button>
                </div>
              </div>
            )}
            {!profileDraft && selected.identity_profile?.profile && (
              <p className="text-xs text-[#117049] font-medium">
                Identity confirmed: {selected.identity_profile.profile.canonical_name || selected.name}
                {selected.identity_profile.profile.current_role && selected.identity_profile.profile.current_role !== 'unknown' && (
                  <> — {selected.identity_profile.profile.current_role}</>
                )}
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-semibold text-[#5b6780] block mb-1.5">Person name</span>
                <input
                  value={selected.name || ''}
                  onChange={e => updateSelected({ name: e.target.value, identity_profile: null }, { debounce: true })}
                  placeholder="Full name"
                  className="w-full text-[13px] border border-[#e1e5ec] rounded-md px-3 focus:outline-none focus:border-[#1c4ed8] h-8"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-[#5b6780] block mb-1.5">Company name</span>
                <input
                  value={selected.company || ''}
                  onChange={e => updateSelected({ company: e.target.value, identity_profile: null }, { debounce: true })}
                  placeholder="Company / organization"
                  className="w-full text-[13px] border border-[#e1e5ec] rounded-md px-3 focus:outline-none focus:border-[#1c4ed8] h-8"
                />
              </label>
            </div>

            <div className="rounded-md border border-[#e1e5ec] bg-[#f6f7fa] p-4">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <h4 className="text-sm font-semibold text-[#0d1524]">Person search filters</h4>
                  <p className="text-xs text-[#5b6780] mt-1">Optional Tavily deep-search terms for this person</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {(selected.search_filters || []).length === 0 ? (
                  <span className="text-sm text-[#76829a]">No filters added</span>
                ) : selected.search_filters.map(filter => (
                  <span key={filter} className="inline-flex items-center gap-1 pl-3 pr-1 py-1.5 rounded-full bg-white border border-[#e1e5ec] text-sm text-[#45516a]">
                    {filter}
                    <button
                      type="button"
                      onClick={() => updateSelected({ search_filters: selected.search_filters.filter(item => item !== filter) })}
                      className="p-1 rounded-full hover:bg-[#fae8e7] hover:text-[#bd2f2b] text-[#76829a]"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2 max-w-lg">
                <input
                  value={filterInput}
                  onChange={e => setFilterInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addSearchFilter(filterInput)}
                  placeholder="legal, fraud, appointment, profile..."
                  className="flex-1 min-w-0 text-[13px] border border-[#e1e5ec] rounded-md px-3 bg-white focus:outline-none focus:border-[#1c4ed8] h-8"
                />
                <button
                  type="button"
                  onClick={() => addSearchFilter(filterInput)}
                  className="px-3 text-[13px] font-medium text-[#1c4ed8] border border-[#c5d3f6] bg-white hover:bg-[#e7edfc] rounded-md h-8"
                >
                  Add
                </button>
              </div>
            </div>

            <div className="rounded-md border border-[#e1e5ec] bg-[#f6f7fa] p-4">
              <h4 className="text-sm font-semibold text-[#0d1524] mb-1">Recent results for this person</h4>
              <p className="text-xs text-[#5b6780] mb-3">If any of these are about someone else with the same name, flag it — future searches will avoid that context.</p>
              {recordsLoading ? (
                <p className="text-xs text-[#76829a]">Loading…</p>
              ) : personRecords.length === 0 ? (
                <p className="text-sm text-[#76829a]">No saved results yet</p>
              ) : (
                <ul className="space-y-2">
                  {personRecords.map(record => (
                    <li key={record.id} className="flex items-center justify-between gap-3 bg-white border border-[#e1e5ec] rounded-lg px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm text-[#45516a] truncate">{record.title}</p>
                        <p className="text-xs text-[#76829a] truncate">{record.source_domain}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => rejectRecord(record.id)}
                        disabled={rejectingId === record.id}
                        className="shrink-0 inline-flex items-center gap-1 text-[13px] font-medium text-[#bd2f2b] hover:bg-[#fae8e7] disabled:opacity-60 rounded-md px-3 h-8"
                      >
                        {rejectingId === record.id ? <Loader2 size={12} className="animate-spin" /> : <ThumbsDown size={12} />}
                        Not this person
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </section>
      </div>
    </div>
  );
}


/* ─── Main ─── */
const MODE_TABS = [
  { key: 'person', label: 'People', icon: UserRound },
  { key: 'sources', label: 'News sources', icon: Link },
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'scheduler', label: 'Scheduler', icon: CalendarClock },
];

const MODE_TITLES = {
  person: ['People', 'Track individuals and the intelligence collected on them'],
  sources: ['News sources', 'Publishers collection trusts, shared by every company'],
  email: ['Email', 'Recipients and recent email activity'],
  scheduler: ['Scheduler', 'Automated brief and digest schedules'],
};

function SettingsView({
  emailActivity = [],
  scheduledReports = [],
  onReportsChange,
  onRefreshRecords,
  initialMode = 'person',
  onModeConsumed,
}) {
  const [mode, setMode] = useState(MODE_TITLES[initialMode] ? initialMode : 'person');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!initialMode) return;
    if (MODE_TITLES[initialMode] && initialMode !== mode) setMode(initialMode);
    onModeConsumed?.();
  }, [initialMode, mode, onModeConsumed]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiFetch('/configuration/search');
        if (!cancelled) setConfig(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!config?.custom_sources) return;
    setCustomSourceFragments(config.custom_sources);
  }, [config?.custom_sources]);

  const handleSourceChange = async (customSources) => {
    const updated = await apiFetch('/configuration/sources', {
      method: 'PUT',
      body: JSON.stringify({ custom_sources: customSources }),
    });
    setCustomSourceFragments(updated.custom_sources || []);
    setConfig(updated);
    setToast('News sources saved');
    setTimeout(() => setToast(''), 2500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-[#76829a]">
        <Loader2 size={26} className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-24 text-[#bd2f2b]">
        <AlertCircle size={26} className="mx-auto mb-2" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  const [, subtitle] = MODE_TITLES[mode];

  return (
    <div className="pb-10">
      <div className="mb-5">
        <h1 className="text-2xl leading-tight font-semibold tracking-tight text-[#0d1524]">Settings</h1>
        <p className="mt-1 text-sm text-[#45516a]">
          People, news sources, email and report schedules. Company search rules are on the Companies page.
        </p>
      </div>

      <div role="tablist" className="flex items-end gap-[22px] border-b border-[#e1e5ec] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {MODE_TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={mode === key}
            onClick={() => setMode(key)}
            className={`flex items-center gap-2 py-2.5 -mb-px text-[13px] whitespace-nowrap border-b-2 ${
 mode === key ? 'text-[#1c4ed8] border-[#1c4ed8] font-medium' : 'text-[#45516a] border-transparent hover:text-[#0d1524]'
 }`}
          >
            <Icon size={14} />{label}
          </button>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-[13px] text-[#76829a]">{subtitle}</p>
        {toast && <span className="text-[12.5px] text-[#117049]">{toast}</span>}
      </div>
      <div className="mt-3">
          {mode === 'person' && <PeopleSettings onRefreshRecords={onRefreshRecords} />}
        {mode === 'sources' && (
          <SourceChannelsSection
            sources={config?.trusted_sources || []}
            customSources={config?.custom_sources || []}
            onChange={handleSourceChange}
          />
        )}
        {mode === 'email' && <EmailCenterView emailActivity={emailActivity} />}
        {mode === 'scheduler' && (
          <ScheduledReportsView reports={scheduledReports} onReportsChange={onReportsChange} />
        )}
      </div>
    </div>
  );
}

export default SettingsView;
